import { getLocalStorage } from '@utils/storage';
import { useQueryParamHelper } from '@utils/queryString';
import { useEffect, useMemo } from 'react';
import { addError } from '@utils/logger';

enum CurrencyCode {
  AUD = 'AUD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  USD = 'USD',
}

enum CultureCode {
  AU = 'AU',
  CA = 'CA',
  DE = 'DE',
  ES = 'ES',
  FR = 'FR',
  IT = 'IT',
  GB = 'GB',
  JA = 'JA',
  US = 'US',
}

enum LanguageCode {
  de = 'de',
  en = 'en',
  es = 'es',
  fr = 'fr',
  it = 'it',
  ja = 'ja',
}

const findMatchingEnumValue = <T>(enumObj: Record<string, T>, key?: string): T | undefined => {
  if (!key) {
    return undefined;
  }

  const actualKey = Object.keys(enumObj).find((val) => val?.toLowerCase() === key.toLowerCase());
  return actualKey ? enumObj[actualKey] : undefined;
};

type GeoBasedLocalePayload = Readonly<{
  culture: CultureCode;
  currency: CurrencyCode;
  language: LanguageCode;
}>;

const DefaultLocalePayload: GeoBasedLocalePayload = {
  currency: CurrencyCode.USD,
  culture: CultureCode.US,
  language: LanguageCode.en,
};

const defaultLocales: ReadonlyArray<GeoBasedLocalePayload> = [
  DefaultLocalePayload,
  {
    culture: CultureCode.AU,
    currency: CurrencyCode.AUD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.DE,
    currency: CurrencyCode.EUR,
    language: LanguageCode.de,
  },
  {
    culture: CultureCode.ES,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.FR,
    currency: CurrencyCode.EUR,
    language: LanguageCode.fr,
  },
  {
    culture: CultureCode.IT,
    currency: CurrencyCode.EUR,
    language: LanguageCode.it,
  },
  {
    culture: CultureCode.US,
    currency: CurrencyCode.USD,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.GB,
    currency: CurrencyCode.GBP,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.JA,
    currency: CurrencyCode.JPY,
    language: LanguageCode.ja,
  },
];

const findClosestMatchingLocale = (
  payload?: Partial<{
    culture: string;
    currency: string;
    language: string;
  }>
): GeoBasedLocalePayload | undefined => {
  if (!payload) {
    return undefined;
  }

  // handle extended language codes (eg. en-AU, es-US)
  const languageCode = payload.language?.slice(0, 2);
  const cultureCode = payload.language?.slice(3, 5);

  let culture = findMatchingEnumValue(CultureCode, payload.culture ?? cultureCode);
  let currency = findMatchingEnumValue(CurrencyCode, payload.currency);
  let language = findMatchingEnumValue(LanguageCode, languageCode);

  // if any properties are missing, fill them in from the default locales
  if (!culture || !currency || !language) {
    defaultLocales.forEach((defaultLocale) => {
      if (defaultLocale.culture === culture || defaultLocale.currency === currency || defaultLocale.language === language) {
        culture = culture || defaultLocale.culture;
        currency = currency || defaultLocale.currency;
        language = language || defaultLocale.language;
      }
    });
  }

  if (culture && currency && language) {
    return {
      culture,
      currency,
      language,
    };
  }

  return undefined;
};

const constructLanguageCodeIso15897 = (language: LanguageCode, culture: CultureCode): string => {
  return `${language}-${culture}`;
};

const getLocallyStoredGeoBasedLocale = (): {
  value: GeoBasedLocalePayload | undefined;
  setValue: (locale: GeoBasedLocalePayload) => void;
} => {
  const localStorage = getLocalStorage();
  const storedLocale = localStorage.getItem('joyLocale');

  const setValue = (updatedLocale: GeoBasedLocalePayload) => {
    const locale = findClosestMatchingLocale(updatedLocale);
    if (locale) {
      localStorage.setItem('joyLocale', JSON.stringify(locale));
    }
  };

  if (!storedLocale) {
    return { value: undefined, setValue };
  }

  try {
    const locale = findClosestMatchingLocale(JSON.parse(storedLocale));
    return { value: locale, setValue };
  } catch (error) {
    addError(error as Error, {});
  }
  return { value: undefined, setValue };
};

const useSearchParamsGeoLocale = (): Partial<GeoBasedLocalePayload> | undefined => {
  const { getValueString } = useQueryParamHelper();

  const culture = getValueString('culture') || getValueString('cul');
  const currency = getValueString('currency') || getValueString('cur');
  const language = getValueString('language') || getValueString('lng') || getValueString('lang') || getValueString('l');

  return findClosestMatchingLocale({ culture, currency, language });
};

const extendLocale = (locale: GeoBasedLocalePayload): GeoBasedLocaleExtended => {
  return {
    ...locale,
    languageCodeIso15897: constructLanguageCodeIso15897(locale.language, locale.culture),
  };
};

type GeoBasedLocaleExtended = GeoBasedLocalePayload & {
  languageCodeIso15897: string;
  countryCodeAlpha2?: string;
  countryName?: string;
};

export const useGeoBasedLocale = (defaultValues?: GeoBasedLocalePayload) => {
  const { value: localValues, setValue: setLocalValues } = getLocallyStoredGeoBasedLocale();
  const searchParams = useSearchParamsGeoLocale();

  const fallbackLocale = defaultValues ?? DefaultLocalePayload;

  const locale = useMemo(() => {
    try {
      const searchParamLocale = findClosestMatchingLocale(searchParams);
      const localStorageLocale = findClosestMatchingLocale(localValues);
      const browserLocale = findClosestMatchingLocale({ language: navigator.language });

      return extendLocale(searchParamLocale ?? localStorageLocale ?? browserLocale ?? fallbackLocale);
    } catch (error) {
      addError(error as Error, {});
      console.error(error);
    }

    return extendLocale(fallbackLocale);
  }, [fallbackLocale, localValues, searchParams]);

  useEffect(() => {
    setLocalValues(locale);
  }, [locale, setLocalValues]);

  return locale;
};
